import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageSquare, Clock, RefreshCw } from 'lucide-react';
import { useFeedback } from '../../context';
import { getSectionLabel, getSentimentInfo } from '../../utils';

export function FeedbackSidebar() {
  const { items, isSidebarOpen, toggleSidebar, refreshFeedback, isRefreshing } = useFeedback();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <AnimatePresence>
      {isSidebarOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggleSidebar}
          />

          {/* Sidebar */}
          <motion.aside
            className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            {/* Header */}
            <div className="bg-hul-navy px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-6 h-6 text-white" />
                <h2 className="font-display text-xl text-white tracking-wide">
                  Your Feedback
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={refreshFeedback}
                  disabled={isRefreshing}
                  className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                  title="Refresh feedback"
                >
                  <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={toggleSidebar}
                  className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <MessageSquare className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="font-heading text-lg text-gray-700 mb-2">
                    No Feedback Yet
                  </h3>
                  <p className="text-gray-500 text-sm">
                    Your comments and questions will appear here as you review the proposal.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {items.map((item) => {
                    const sentimentInfo = getSentimentInfo(item.sentiment);

                    return (
                      <motion.div
                        key={item.id}
                        className="p-5 hover:bg-gray-50 transition-colors"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {/* Header */}
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{sentimentInfo.icon}</span>
                            <span className="text-sm font-medium text-gray-700">
                              {getSectionLabel(item.section)}
                            </span>
                            {item.lineNumber && (
                              <span className="text-xs text-gray-400">
                                Line #{item.lineNumber}
                              </span>
                            )}
                          </div>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded ${
                              item.sentiment === 'positive'
                                ? 'bg-green-100 text-green-700'
                                : item.sentiment === 'concern'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}
                          >
                            {sentimentInfo.label}
                          </span>
                        </div>

                        {/* Comment */}
                        <p className="text-gray-700 mb-3">{item.comment}</p>

                        {/* Timestamp */}
                        <div className="flex items-center gap-1.5 text-xs text-gray-400">
                          <Clock className="w-3.5 h-3.5" />
                          {formatDate(item.createdAt)}
                        </div>

                        {/* Response (if any) */}
                        {item.response && (
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-blue-600 font-medium">
                                Response from Sales Rep
                              </span>
                              {item.respondedAt && (
                                <span className="text-xs text-blue-400">
                                  {formatDate(item.respondedAt)}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-blue-900">{item.response}</p>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="border-t border-gray-100 p-4 bg-gray-50">
                <p className="text-sm text-gray-500 text-center">
                  {items.length} comment{items.length !== 1 ? 's' : ''} submitted
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
